import ReactGA from 'react-ga4';
import { numify } from 'numify';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
/////////////////////////////////////////////////////////////
import { useCustomDispatch, useCustomSelector } from '@/core/redux/hooks';
import { telegramWebApp } from '@/main';
import {
	Drawer,
	DrawerContent,
	DrawerClose,
	DrawerFooter,
	DrawerHeader,
	DrawerTitle,
	Button,
	Close,
	CoinImage,
	DrawerDescription,
} from '@/app/shared/components';
import { HackService } from '@/core/services';
import { UpgradeEnergyTankResponse } from '@/core/interfaces';
import {
	setCounter,
	setDiamonds,
	setEnergyTankCostLevel,
	setEnergyTankLevel,
	setMaxEnergy,
	setStickerWonDialog,
	setTotalCoinsGenerated,
} from '@/core/redux/slices';
interface EnergyTankProps {
	isOpen: boolean;
	onClose: React.Dispatch<React.SetStateAction<boolean>>;
	image: string;
}

export const EnergyTank: React.FC<EnergyTankProps> = ({
	isOpen,
	onClose,
	image,
}: EnergyTankProps) => {
	/*********************
	// #region Hooks
	**********************/
	const dispatch = useCustomDispatch();
	const { t } = useTranslation();

	/*********************
	// #region Redux
	**********************/
	const { counter } = useCustomSelector((state) => state.counter);
	const { energyTankCostLevelUp, energyTankLevel } = useCustomSelector(
		(state) => state.hacks
	);

	/*********************
	// #region Functions
	**********************/
	const handleRequestUpgradeEnergyTank = async () => {
		telegramWebApp.HapticFeedback.impactOccurred('heavy');
		if (counter < energyTankCostLevelUp) {
			toast.warning(t('general.notEnough'));
			return;
		}
		toast.promise(HackService.upgradeEnergyTank(), {
			loading: t('general.loading'),
			success: (data: UpgradeEnergyTankResponse) => {
				// Emit event to Google Analytics
				ReactGA.event({
					category: 'boosts',
					action: 'Upgrade energy tank',
				});

				// Check if has a gift
				if (data.isAwardWinning) {
					dispatch(setStickerWonDialog(true));
				}
				dispatch(setTotalCoinsGenerated(data.totalCoinsEarned));
				dispatch(setCounter(data.currentCoins));
				dispatch(setDiamonds(data.currentDiamonds));
				dispatch(setEnergyTankLevel(data.data.level));
				dispatch(setEnergyTankCostLevel(data.data.nextPrice));
				dispatch(setMaxEnergy(data.data.maxEnergy));

				return t('hacks.energyTank.upgradeTankSuccess');
			},
			error: t('hacks.energyTank.upgradeTankError'),
		});

		onClose(false);
	};

	/*********************
	// #region Template
	**********************/
	return (
		<Drawer open={isOpen} onOpenChange={onClose}>
			<DrawerContent>
				<div className="mx-auto mt-4 h-2 w-[100px] rounded-full bg-muted" />
				<div className="mx-auto w-full max-w-sm ">
					<DrawerHeader>
						<DrawerTitle>
							<VisuallyHidden>
								{t('hacks.energyTank.title')}
							</VisuallyHidden>
							<DrawerClose className="right-5 text-right absolute">
								<Close fillcolor="#fff" />
							</DrawerClose>
						</DrawerTitle>
					</DrawerHeader>
					<DrawerDescription>
						<VisuallyHidden>
							{t('hacks.energyTank.description')}{' '}
							{energyTankLevel + 1}.
						</VisuallyHidden>
					</DrawerDescription>
					<div className="mx-auto px-8">
						<img
							className="cursor-pointer mx-auto w-[100px] mb-6"
							src={image}
						/>

						<div className="flex flex-col text-center gap-3 mb-8">
							<p className="font-bold text-2xl">
								{t('hacks.energyTank.title')}
							</p>
							<p className="font-semibold text-sm text-white text-center">
								{t('hacks.energyTank.description')}{' '}
								{energyTankLevel + 1}.
							</p>
						</div>

						<div className="flex my-4 justify-center">
							<CoinImage alt="Recharge Full Energy" />

							<p className="text-lg font-bold">
								{numify(energyTankCostLevelUp)}{' '}
								{t('general.toLevel')} {energyTankLevel + 1}
							</p>
						</div>
					</div>
					<DrawerFooter className="mb-5">
						<DrawerClose asChild>
							<Button
								variant="primary"
								className="py-6 w-full text-sm rounded-lg"
								onClick={handleRequestUpgradeEnergyTank}
							>
								{t('general.upgrade')}
							</Button>
						</DrawerClose>
					</DrawerFooter>
				</div>
			</DrawerContent>
		</Drawer>
	);
};
